import React, { useState, useEffect } from 'react';
import SafetyService, { SAFETY_CATEGORIES, EMERGENCY_TYPES, INJURY_SEVERITY } from '../../services/safetyService';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  ChartBarIcon,
  UserGroupIcon,
  CalendarIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';

const SafetyManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [safetyProtocols, setSafetyProtocols] = useState([]);
  const [emergencyProcedures, setEmergencyProcedures] = useState([]);
  const [safetyStats, setSafetyStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    priority: '',
    description: '',
    steps: [{ title: '', description: '' }],
    warningSigns: ''
  });
  const [emergencyFormData, setEmergencyFormData] = useState({
    title: '',
    type: '',
    priority: '',
    description: '',
    steps: [''],
    contacts: [{ role: '', number: '' }]
  });

  useEffect(() => {
    loadSafetyData();
  }, []);

  const loadSafetyData = async () => {
    try {
      setLoading(true);
      const [protocols, procedures, stats] = await Promise.all([
        SafetyService.getSafetyProtocols(),
        SafetyService.getEmergencyProcedures(),
        SafetyService.getSafetyStatistics()
      ]);
      
      setSafetyProtocols(protocols);
      setEmergencyProcedures(procedures);
      setSafetyStats(stats);
    } catch (error) {
      console.error('Error loading safety data:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleEditItem = (item, type) => {
    setSelectedItem(item);
    setModalType(type);
    setShowModal(true);
  };

  const handleDeleteItem = async (id, type) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        switch (type) {
          case 'protocol':
            await SafetyService.deleteSafetyProtocol(id);
            break;
          case 'emergency':
            await SafetyService.deleteEmergencyProcedure(id);
            break;
          case 'injury-report':
            // Injury report deletion would be implemented here
            console.log(`Deleting injury report with id:`, id);
            break;
          default:
            console.log(`Deleting ${type} with id:`, id);
        }
        await loadSafetyData();
      } catch (error) {
        console.error('Error deleting item:', error);
      }
    }
  };

  const handleCreateProtocol = () => {
    setSelectedItem(null);
    setModalType('protocol');
    setFormData({
      title: '',
      category: '',
      priority: '',
      description: '',
      steps: [{ title: '', description: '' }],
      warningSigns: ''
    });
    setShowModal(true);
  };

  const handleCreateEmergencyProcedure = () => {
    setSelectedItem(null);
    setModalType('emergency');
    setEmergencyFormData({
      title: '',
      type: '',
      priority: '',
      description: '',
      steps: [''],
      contacts: [{ role: '', number: '' }]
    });
    setShowModal(true);
  };

  const handleSubmitProtocol = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.category || !formData.priority || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const protocolData = {
        id: selectedItem ? selectedItem.id : Date.now(),
        title: formData.title,
        category: formData.category,
        priority: formData.priority,
        description: formData.description,
        steps: formData.steps.filter(step => step.title || step.description),
        warningSigns: formData.warningSigns.split('\n').filter(sign => sign.trim()),
        dateCreated: selectedItem ? selectedItem.dateCreated : new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      
      if (selectedItem) {
        await SafetyService.updateSafetyProtocol(selectedItem.id, protocolData);
      } else {
        await SafetyService.createSafetyProtocol(protocolData);
      }
      
      setShowModal(false);
      await loadSafetyData();
      console.log('Protocol saved successfully');
    } catch (error) {
      console.error('Error saving protocol:', error);
      alert('Error saving protocol. Please try again.');
    }
  };

  const handleSubmitEmergencyProcedure = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!emergencyFormData.title || !emergencyFormData.type || !emergencyFormData.priority || !emergencyFormData.description) {
      alert('Please fill in all required fields');
      return;
    }
    
    try {
      const procedureData = {
        id: selectedItem ? selectedItem.id : Date.now(),
        title: emergencyFormData.title,
        type: emergencyFormData.type,
        priority: emergencyFormData.priority,
        description: emergencyFormData.description,
        steps: emergencyFormData.steps.filter(step => step.trim()),
        contacts: emergencyFormData.contacts.filter(contact => contact.role || contact.number),
        dateCreated: selectedItem ? selectedItem.dateCreated : new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };
      
      if (selectedItem) {
        await SafetyService.updateEmergencyProcedure(selectedItem.id, procedureData);
      } else {
        await SafetyService.createEmergencyProcedure(procedureData);
      }
      
      setShowModal(false);
      await loadSafetyData();
      console.log('Emergency procedure saved successfully');
    } catch (error) {
      console.error('Error saving emergency procedure:', error);
      alert('Error saving emergency procedure. Please try again.');
    }
  };



  const OverviewTab = () => (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-100">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Safety Protocols</p>
              <p className="text-2xl font-semibold text-gray-900">{safetyStats.protocolsCount || 0}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-100">
              <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Emergency Procedures</p>
              <p className="text-2xl font-semibold text-gray-900">{safetyStats.emergencyProceduresCount || 0}</p>
            </div>
          </div>
        </div>
        

        
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-100">
              <UserGroupIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Athletes</p>
              <p className="text-2xl font-semibold text-gray-900">245</p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Injury Reports</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <p className="text-gray-500 text-center py-4">No recent injury reports available</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Injury Breakdown by Sport</h3>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {Object.entries(safetyStats.sportBreakdown || {}).map(([sport, count]) => (
                <div key={sport} className="flex items-center justify-between">
                  <span className="text-gray-700">{sport}</span>
                  <span className="font-semibold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ProtocolsTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Safety Protocols</h2>
        <button
          onClick={handleCreateProtocol}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Protocol</span>
        </button>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Protocol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Updated
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {safetyProtocols.map(protocol => (
              <tr key={protocol.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{protocol.title}</div>
                    <div className="text-sm text-gray-500">{protocol.description}</div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {protocol.category.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${SafetyService.getPriorityColor(protocol.priority)}`}>
                    {protocol.priority.toUpperCase()}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {SafetyService.formatDate(protocol.lastUpdated)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditItem(protocol, 'protocol')}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteItem(protocol.id, 'protocol')}
                      className="text-red-600 hover:text-red-900"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );



  const EmergencyTab = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Emergency Procedures</h2>
        <button
          onClick={handleCreateEmergencyProcedure}
          className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center space-x-2"
        >
          <PlusIcon className="h-5 w-5" />
          <span>Add Procedure</span>
        </button>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {emergencyProcedures.map(procedure => (
          <div key={procedure.id} className="bg-white rounded-lg shadow-md border border-gray-200">
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${SafetyService.getPriorityColor(procedure.priority)}`}>
                    <ExclamationTriangleIcon className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{procedure.title}</h3>
                    <p className="text-gray-600 text-sm">{procedure.description}</p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditItem(procedure, 'emergency')}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteItem(procedure.id, 'emergency')}
                    className="text-red-600 hover:text-red-900"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <div className="mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${SafetyService.getPriorityColor(procedure.priority)}`}>
                  {procedure.priority.toUpperCase()} PRIORITY
                </span>
              </div>
              
              {procedure.steps && (
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Key Steps:</h4>
                  <ul className="list-disc list-inside space-y-1 text-sm text-gray-600">
                    {procedure.steps.slice(0, 3).map((step, index) => (
                      <li key={index}>{step}</li>
                    ))}
                    {procedure.steps.length > 3 && (
                      <li className="text-blue-600">+ {procedure.steps.length - 3} more steps</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading safety management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <ShieldCheckIcon className="h-8 w-8 mr-3 text-red-600" />
            Safety Management
          </h1>
          <p className="text-gray-600 mt-2">
            Manage safety protocols, injury reports, and emergency procedures
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('protocols')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'protocols'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Safety Protocols
            </button>

            <button
              onClick={() => setActiveTab('emergency')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'emergency'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Emergency Procedures
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && <OverviewTab />}
        {activeTab === 'protocols' && <ProtocolsTab />}
        {activeTab === 'emergency' && <EmergencyTab />}
      </div>

      {/* Safety Protocol Modal */}
      {showModal && modalType === 'protocol' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Edit Safety Protocol' : 'Add New Safety Protocol'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Protocol Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter protocol title"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category *
                </label>
                <select 
                  value={formData.category}
                  onChange={(e) => setFormData({...formData, category: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select category</option>
                  <option value="concussion">Concussion</option>
                  <option value="injury_prevention">Injury Prevention</option>
                  <option value="emergency">Emergency</option>
                  <option value="heat_illness">Heat Illness</option>
                  <option value="equipment">Equipment</option>
                  <option value="general">General</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority *
                </label>
                <select 
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter protocol description"
                />
              </div>

              {/* Steps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Protocol Steps
                </label>
                <div className="space-y-3">
                  {formData.steps.map((step, index) => (
                    <div key={index} className="border border-gray-200 rounded-md p-3">
                      <input
                        type="text"
                        value={step.title}
                        onChange={(e) => {
                          const newSteps = [...formData.steps];
                          newSteps[index].title = e.target.value;
                          setFormData({...formData, steps: newSteps});
                        }}
                        placeholder="Step title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
                      />
                      <textarea
                        rows={2}
                        value={step.description}
                        onChange={(e) => {
                          const newSteps = [...formData.steps];
                          newSteps[index].description = e.target.value;
                          setFormData({...formData, steps: newSteps});
                        }}
                        placeholder="Step description"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      {formData.steps.length > 1 && (
                        <button
                          type="button"
                          onClick={() => {
                            const newSteps = formData.steps.filter((_, i) => i !== index);
                            setFormData({...formData, steps: newSteps});
                          }}
                          className="text-red-600 hover:text-red-800 text-sm mt-2"
                        >
                          Remove Step
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => {
                      setFormData({
                        ...formData,
                        steps: [...formData.steps, { title: '', description: '' }]
                      });
                    }}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    + Add Another Step
                  </button>
                </div>
              </div>

              {/* Warning Signs */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Warning Signs (optional)
                </label>
                <textarea
                  rows={3}
                  value={formData.warningSigns}
                  onChange={(e) => setFormData({...formData, warningSigns: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter warning signs, one per line"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitProtocol}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {selectedItem ? 'Update Protocol' : 'Create Protocol'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Emergency Procedure Modal */}
      {showModal && modalType === 'emergency' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                {selectedItem ? 'Edit Emergency Procedure' : 'Add New Emergency Procedure'}
              </h3>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Procedure Title *
                </label>
                <input
                  type="text"
                  value={emergencyFormData.title}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, title: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter procedure title"
                />
              </div>

              {/* Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Type *
                </label>
                <select 
                  value={emergencyFormData.type}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select type</option>
                  <option value="medical">Medical</option>
                  <option value="weather">Weather</option>
                  <option value="facility">Facility</option>
                  <option value="security">Security</option>
                </select>
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority *
                </label>
                <select 
                  value={emergencyFormData.priority}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, priority: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select priority</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <textarea
                  rows={3}
                  value={emergencyFormData.description}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter procedure description"
                />
              </div>

              {/* Steps */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Procedure Steps *
                </label>
                <textarea
                  rows={5}
                  value={emergencyFormData.steps.join('\n')}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, steps: e.target.value.split('\n')})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter procedure steps, one per line"
                />
              </div>

              {/* Emergency Contacts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Emergency Contacts (optional)
                </label>
                <textarea
                  rows={3}
                  value={emergencyFormData.contacts.join('\n')}
                  onChange={(e) => setEmergencyFormData({...emergencyFormData, contacts: e.target.value.split('\n')})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter emergency contacts (Role: Phone Number)"
                />
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end space-x-3 mt-6 pt-4 border-t border-gray-200">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitEmergencyProcedure}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                {selectedItem ? 'Update Procedure' : 'Create Procedure'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SafetyManagement;